/* eslint-disable no-shadow */
import _ from 'lodash';
import types from './mutation-types';
import { traceTemplate } from '../../../utils/constants';

const state = {
  count: 1,
  byId: {},
  allIds: [],
  selectedInstruments: [],
};

const getters = {
  find: (state) => (id) => state.byId[id],
  list: (state, getters) => state.allIds.map((id) => getters.find(id)),
  count: (state) => state.allIds.length,
  currentId: (state) => state.count - 1,
  findByUUID: (param, getters) => (uuid) => {
    const allTraces = getters.list;
    if (allTraces.length === 0) return [];
    return allTraces.filter((trace) => trace.chart_uuid === uuid);
  },
  findSelectedInstruments: (param, getters) => (id) => {
    const trace = getters.find(id);
    if (trace) {
      return _.filter(state.selectedInstruments, { id });
    }
    return [];
  },
  getSelectedAxis: (param, getters) => (id) => {
    const trace = getters.find(id);
    if (trace) {
      return trace.selectedAxis;
    }
    return {
      x: null,
      y: null,
      z: null,
      color: {
        active: false,
        value: null,
        range: {
          min: null,
          max: null,
        },
        colorbar: 'thermal',
      },
    };
  },
  getDateTimeRange: (param, getters) => (id) => {
    const trace = getters.find(id);
    if (trace) {
      return trace.dateTimeRange;
    }
    return {
      start: {
        date: '',
        time: '',
      },
      end: {
        date: '',
        time: '',
      },
      tz: 'UTC',
      min: '',
      max: '',
    };
  },
  exists: (state) => (id) => _.has(state.byId, id),
};

const mutations = {
  [types.ADD_TRACE]: async (state, { trace, override = false }) => {
    state.byId[trace.id] = trace;
    if (state.allIds.includes(trace.id)) return;
    state.allIds.push(trace.id);
    if (!override) {
      // mutate count only when NOT overriding
      // eslint-disable-next-line no-plusplus
      state.count++;
    }
  },
  [types.REMOVE_TRACE]: (state, { id }) => {
    const traceIndex = state.allIds.indexOf(id);
    if (traceIndex !== -1) {
      delete state.byId[id];
      state.allIds.splice(traceIndex, 1);
    }
  },
  [types.UPDATE_TRACE]: (state, { id, newTrace }) => {
    state.byId = {
      ...state.byId,
      [id]: newTrace,
    };
  },
  [types.RESET_TRACE]: (state, { id }) => {
    let newTrace = _.defaultsDeep({}, traceTemplate);
    const trace = state.byId[id];
    newTrace = {
      ...newTrace,
      id: trace.id,
      chart_uuid: trace.chart_uuid,
    };
    state.byId = {
      ...state.byId,
      [id]: newTrace,
    };
  },
  [types.ADD_INSTRUMENT]: (state, { id, ref }) => {
    state.selectedInstruments.push({ id, ref });
  },
  [types.REMOVE_INSTRUMENT]: (state, { id, ref }) => {
    state.selectedInstruments = _.remove(
      state.selectedInstruments,
      (o) => !_.isEqual(o, { id, ref }),
    );
  },
  [types.UPDATE_TAB_INDEX]: (state, { id, tabIndex }) => {
    state.byId[id].tabIndex = tabIndex;
  },
  [types.UPDATE_SELECTED_AXES]: (state, { id, axis, value }) => {
    const trace = state.byId[id];
    const newTrace = {
      ...trace,
    };
    const finalAxis = `selectedAxis.${axis}`;
    _.set(newTrace, finalAxis, value);
    state.byId = {
      ...state.byId,
      [id]: newTrace,
    };
  },
  [types.UPDATE_DATETIME]: (state, { id, axis, value }) => {
    const trace = state.byId[id];
    const newTrace = {
      ...trace,
    };
    const finalAxis = `dateTimeRange.${axis}`;
    _.set(newTrace, finalAxis, value);
    state.byId = {
      ...state.byId,
      [id]: newTrace,
    };
  },
  [types.UPDATE_ATTRIBUTE]: (state, { id, attr, value }) => {
    const trace = state.byId[id];
    const newTrace = {
      ...trace,
    };
    _.set(newTrace, attr, value);
    state.byId = {
      ...state.byId,
      [id]: newTrace,
    };
  },
};

const actions = {
  addTrace: async ({ commit, state }, { uuid, trace, override = false }) => {
    const newTrace = {
      ...trace,
    };
    if (!override) {
      while (_.has(state.byId, state.count)) {
        // When not overriding, check if current id
        // is not in state yet in case it was overridden
        // if it is, then skip to the next count
        // eslint-disable-next-line no-plusplus
        state.count++;
      }
      newTrace.id = state.count;
    }
    newTrace.chart_uuid = uuid;
    await commit(types.ADD_TRACE, { trace: newTrace, override });
    return newTrace;
  },
  removeTrace: ({ commit }, { id }) => {
    // Action for removing a single chart based on uuid
    commit(types.REMOVE_TRACE, { id });
  },
  updateTrace: ({ commit, state }, { id, updateObj }) => {
    const newTrace = {
      ...state.byId[id],
      ...updateObj,
    };
    commit(types.UPDATE_TRACE, { id, newTrace });
    // const tempCharts = [...state.charts];
    // const { chart } = _.find(tempCharts, ['i', uuid]);
    // const newtrace = _.find(chart.data, ['id', traceId]);
    // newtrace.formOptions = { ...formOptions };
    // newtrace.requestForm = requestForm;
    // // In TraceForm to check whenever parameters are selected
    // // Sets the download btn to enable only when x and y axis in form have
    // // values
    // if (!_.isNull(newtrace.formOptions.selectedAxis.x && newtrace.formOptions.selectedAxis.y)) {
    //     newtrace.downloadBtnValue = true;
    // }
  },
  resetTrace: ({ commit }, { id }) => {
    commit(types.RESET_TRACE, { id });
  },
  addInstrument: ({ commit }, { id, ref }) => {
    commit(types.ADD_INSTRUMENT, { id, ref });
  },
  removeInstrument: ({ commit }, { id, ref }) => {
    commit(types.REMOVE_INSTRUMENT, { id, ref });
  },
  updateTabIndex: async ({ commit }, { id, tabIndex }) => {
    commit(types.UPDATE_TAB_INDEX, { id, tabIndex });
  },
  updateSelected: ({ commit }, { id, axis, value }) => {
    commit(types.UPDATE_SELECTED_AXES, { id, axis, value });
  },
  updateX: ({ commit }, { id, value }) => {
    commit(types.UPDATE_SELECTED_AXES, { id, axis: 'x', value });
  },
  updateY: ({ commit }, { id, value }) => {
    commit(types.UPDATE_SELECTED_AXES, { id, axis: 'y', value });
  },
  updateColor: ({ commit }, { id, value }) => {
    commit(types.UPDATE_SELECTED_AXES, { id, axis: 'color', value });
  },
  updateStartDate: ({ commit }, { id, value }) => {
    commit(types.UPDATE_DATETIME, { id, axis: 'start.date', value });
  },
  updateStartTime: ({ commit }, { id, value }) => {
    commit(types.UPDATE_DATETIME, { id, axis: 'start.time', value });
  },
  updateEndDate: ({ commit }, { id, value }) => {
    commit(types.UPDATE_DATETIME, { id, axis: 'end.date', value });
  },
  updateEndTime: ({ commit }, { id, value }) => {
    commit(types.UPDATE_DATETIME, { id, axis: 'end.time', value });
  },
  updateAttribute: ({ commit }, { id, attr, value }) => {
    commit(types.UPDATE_ATTRIBUTE, { id, attr, value });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
